import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  listProjects,
  deleteProject,
  togglePublished,
  setDisplayOrder,
} from '../adminApi';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';

// ── Order cell ────────────────────────────────────────────────────────────────
// Smart-swap: typing 2 for a project currently at 4 swaps them.
// Input is clamped to [1, total] on save.
function OrderCell({ projectId, initialValue, total, onSwapped }) {
  const [value, setValue] = useState(String(initialValue ?? 1));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(String(initialValue ?? 1));
  }, [initialValue]);

  const save = useCallback(async () => {
    const num = parseInt(value, 10);
    const clamped = Math.max(1, Math.min(num, total));

    if (isNaN(num)) {
      setValue(String(initialValue ?? 1));
      return;
    }
    if (clamped === initialValue) {
      setValue(String(clamped));
      return;
    }

    setSaving(true);
    try {
      const res = await setDisplayOrder(projectId, clamped);
      // res.updates = [{id, display_order}, ...] for every record that changed
      onSwapped(res.updates || []);
      toast.success('Proje Sırası güncellendi.');
    } catch (err) {
      toast.error(err.message);
      setValue(String(initialValue ?? 1));
    } finally {
      setSaving(false);
    }
  }, [value, initialValue, projectId, total, onSwapped]);

  return (
    <input
      type="number"
      min={1}
      max={total}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
      disabled={saving}
      title={`1 ile ${total} arasında bir değer girin. Girilen konumdaki proje ile yer değiştirilir.`}
      className="w-14 bg-white/[0.04] border border-white/[0.07] rounded px-2 py-1 text-sm text-white text-center focus:outline-none focus:border-[#c5a059]/50 disabled:opacity-40 appearance-none"
    />
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  ongoing:   'bg-blue-500/15 text-blue-300 border-blue-500/20',
  completed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20',
  planning:  'bg-yellow-500/15 text-yellow-300 border-yellow-500/20',
};

function StatusBadge({ status, label }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${
      STATUS_COLORS[status] || 'bg-white/10 text-white/60 border-white/10'
    }`}>
      {label || status}
    </span>
  );
}

// ── Publish toggle ────────────────────────────────────────────────────────────
function PublishChip({ active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border transition-colors ${
        active
          ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20'
          : 'bg-white/[0.04] text-white/30 border-white/[0.07] hover:border-white/20'
      }`}
    >
      {active ? 'Yayında' : 'Taslak'}
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listProjects()
      .then(setProjects)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleTogglePublished = async (id) => {
    try {
      const res = await togglePublished(id);
      setProjects((ps) =>
        ps.map((p) => (p.id === id ? { ...p, is_published: res.is_published } : p))
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteProject(id);
      // Backend normalizes orders after delete — re-fetch for accurate state
      setDeleteId(null);
      toast.success('Proje silindi.');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Apply swap result: update display_order for every record that changed
  const applySwap = useCallback((updates) => {
    if (!updates?.length) return;
    setProjects((ps) =>
      ps.map((p) => {
        const hit = updates.find((u) => u.id === p.id);
        return hit ? { ...p, display_order: hit.display_order } : p;
      }).sort((a, b) => a.display_order - b.display_order)
    );
  }, []);

  if (loading) return <PageLoader />;
  if (error)   return <PageError message={error} />;

  const total = projects.length;

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">Projeler</h1>
          <p className="text-white/35 text-sm mt-0.5">{total} proje</p>
        </div>
        <Link
          to="/admin/projects/new"
          className="flex items-center gap-2 bg-[#c5a059] hover:bg-[#d4af6a] text-black font-semibold px-4 py-2 rounded-lg text-sm transition-colors"
        >
          <Plus size={15} />
          Yeni Proje
        </Link>
      </div>

      {/* Legend */}
      <p className="text-[11px] text-white/25 mb-4">
        <strong className="text-white/40">Proje Sırası</strong> — her yerde aynı sıralama: /projeler sayfası ve anasayfa vitrini.
        Değiştirince o konumdaki proje ile yer değişir. Değer 1–{total || 'N'} arasında olmalıdır. — Enter veya dışarı tıkla → kaydet
      </p>

      {total === 0 ? (
        <EmptyState icon={Building2} text="Henüz proje yok." />
      ) : (
        <div className="bg-[#111] border border-white/[0.07] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
          <div className="min-w-[580px]">
          {/* Table head */}
          <div className="grid grid-cols-[2fr_1fr_1fr_70px_80px_100px] gap-3 px-4 py-2.5 border-b border-white/[0.06] text-[10px] text-white/30 uppercase tracking-wider">
            <span>Proje</span>
            <span>Konum / Yıl</span>
            <span>Durum</span>
            <span className="text-center" title="1 = anasayfa vitrini ilk sıra · 2 = ikinci sıra · sıra arttıkça projeler sayfasında alta iner">Sıra</span>
            <span className="text-center">Yayın</span>
            <span className="text-right">İşlemler</span>
          </div>

          {/* Rows */}
          {projects.map((p) => (
            <div
              key={p.id}
              className="grid grid-cols-[2fr_1fr_1fr_70px_80px_100px] gap-3 px-4 py-3 border-b border-white/[0.04] last:border-0 items-center hover:bg-white/[0.015] transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{p.title}</p>
                <p className="text-[11px] text-white/30 truncate">{p.slug}</p>
              </div>

              <div className="min-w-0">
                <p className="text-sm text-white/65 truncate">{p.location}</p>
                <p className="text-[11px] text-white/30">{p.year}</p>
              </div>

              <div>
                <StatusBadge status={p.status} label={p.status_label} />
              </div>

              <div className="flex justify-center">
                <OrderCell
                  projectId={p.id}
                  initialValue={p.display_order}
                  total={total}
                  onSwapped={applySwap}
                />
              </div>

              <div className="flex justify-center">
                <PublishChip
                  active={p.is_published}
                  onClick={() => handleTogglePublished(p.id)}
                />
              </div>

              <div className="flex items-center justify-end gap-1">
                {deleteId === p.id ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleDelete(p.id)}
                      className="text-[11px] bg-red-500/15 text-red-400 border border-red-500/20 px-2 py-1 rounded-lg hover:bg-red-500/25 transition-colors"
                    >
                      Sil
                    </button>
                    <button
                      onClick={() => setDeleteId(null)}
                      className="text-[11px] text-white/35 hover:text-white/60 px-1.5 py-1"
                    >
                      İptal
                    </button>
                  </div>
                ) : (
                  <>
                    <Link
                      to={`/admin/projects/${p.id}/edit`}
                      className="p-1.5 rounded-lg text-white/35 hover:text-[#c5a059] hover:bg-[#c5a059]/10 transition-colors"
                      title="Düzenle"
                    >
                      <Pencil size={14} />
                    </Link>
                    <button
                      onClick={() => setDeleteId(p.id)}
                      className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Sil"
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PageLoader() {
  return (
    <div className="p-8 flex items-center justify-center min-h-64">
      <div className="w-5 h-5 border-2 border-[#c5a059]/30 border-t-[#c5a059] rounded-full animate-spin" />
    </div>
  );
}

function PageError({ message }) {
  return (
    <div className="p-4 md:p-8">
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-5 py-4 text-red-400 text-sm">
        Hata: {message}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-white/20">
      <Icon size={32} className="mb-3" />
      <p className="text-sm">{text}</p>
    </div>
  );
}
